'use client';

import { useEffect, useMemo } from 'react';

import { NOTIFICATION_CONFIG } from '@website/config/notifications.config';
import { useUserType } from '@website/hooks/useCurrentUser';
import { filterNotificationsByAccountMode } from '@website/utils/notification.utils';

import { type Notification, useNotifications } from './useNotifications';

/**
 * Update the OS app badge to reflect the unread count.
 *
 * Browser support is partial (Chrome / Edge / Safari macOS /
 * iOS Safari 16.4+ on installed PWA). When unavailable, this is a no-op.
 *
 * Background updates (when no tab is open) are driven by the service
 * worker from the push payload's `unreadCount`. This is the foreground
 * complement: the open tab keeps the badge tracking the source of truth.
 */
function syncAppBadge(count: number): void {
  if (typeof navigator === 'undefined') return;
  const nav = navigator as Navigator & {
    setAppBadge?: (count?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  try {
    if (count > 0 && nav.setAppBadge) {
      void nav.setAppBadge(count);
    } else if (nav.clearAppBadge) {
      void nav.clearAppBadge();
    }
  } catch {
    /* badging API throws on permission edge cases - safe to ignore */
  }
}

/**
 * Mode-aware unread notification count and filtered notifications.
 *
 * Fetches notifications, filters them by the current account mode
 * (professional vs learner), and returns the unread count plus
 * the filtered list. Both the header and bottom nav use this hook
 * so the badge count is always consistent.
 */
export function useUnreadCount(options?: { enablePolling?: boolean; enabled?: boolean }) {
  const { enablePolling = false, enabled = true } = options || {};
  const userType = useUserType();
  const isProfessional = userType === 'professional';
  const isAuthenticated = userType !== 'public';

  const {
    data: allNotifications = [],
    isLoading,
    ...rest
  } = useNotifications(
    { limit: NOTIFICATION_CONFIG.DROPDOWN_NOTIFICATION_LIMIT },
    { enablePolling, enabled: enabled && isAuthenticated }
  );

  const notifications = useMemo(
    () => filterNotificationsByAccountMode(allNotifications, isProfessional),
    [allNotifications, isProfessional]
  );

  const count = useMemo(() => notifications.filter((n: Notification) => !n.read).length, [notifications]);

  useEffect(() => {
    if (!isAuthenticated) return;
    syncAppBadge(count);
  }, [count, isAuthenticated]);

  return { count, notifications, isLoading, ...rest };
}
